import React, { useState, useCallback, useRef, lazy, Suspense } from "react";
import copyIcon from "../../assets/img/copy.png";
import useBalanceData from "./hooks/useBalanceData";
import useTransactionData from "./hooks/useTransactionData";
import BalanceSummary from "./components/BalanceSummary";
import QuickActions from "./components/QuickActions";
import RecentTransactions from "./components/RecentTransactions";
import TransactionMetrics from "./components/TransactionMetrics";
import WithdrawalGate from "./components/WithdrawalGate";
import NotificationsHost from "./components/NotificationsHost";
import styles from "./ModernDashboard.module.css";

const DepositModal = lazy(() => import("components/DepositModal"));
const ChatWidget = lazy(() => import("components/ChatWidget/ChatWidget"));

/**
 * ModernDashboard — Fintech/SaaS redesign.
 * Uses CSS modules for UI, skeleton loaders during fetch,
 * mobile-first responsive layout.
 */
export default function ModernDashboard() {
  /* ---- data hooks ---- */
  const {
    availableBalance,
    refundableBalance,
    pendingPayment,
    tcc,
    status,
    loading: balanceLoading,
    currentUser,
  } = useBalanceData();

  const { transactions, recentTransactions, loading: txLoading } = useTransactionData();

  /* ---- modal state ---- */
  const [showDeposit, setShowDeposit] = useState(false);
  const [showWithdrawalGate, setShowWithdrawalGate] = useState(false);

  /* ---- rate-limiting ---- */
  const lastActionRef = useRef(0);
  const RATE_LIMIT_MS = 2000;

  const throttleAction = useCallback((fn) => {
    const now = Date.now();
    if (now - lastActionRef.current < RATE_LIMIT_MS) return;
    lastActionRef.current = now;
    fn();
  }, []);

  /* ---- callbacks ---- */
  const openDeposit = useCallback(() => {
    throttleAction(() => setShowDeposit(true));
  }, [throttleAction]);

  const closeDeposit = useCallback(() => setShowDeposit(false), []);

  const openWithdrawalGate = useCallback(() => {
    throttleAction(() => {
      const pp = parseFloat(pendingPayment) || 0;
      if (pp > 0) {
        setShowWithdrawalGate(true);
      } else {
        window.location.href = "/user/support";
      }
    });
  }, [pendingPayment, throttleAction]);

  const closeWithdrawalGate = useCallback(
    () => setShowWithdrawalGate(false),
    []
  );

  const handlePayNow = useCallback(() => {
    setShowWithdrawalGate(false);
    setShowDeposit(true);
  }, []);

  const handleProceedToWithdraw = useCallback(() => {
    window.location.href = "/user/support";
  }, []);

  return (
    <>
      {/* Notification toasts */}
      <NotificationsHost />

      <div className="content mid-content">
        {/* ---- Welcome Row ---- */}
        <div className={styles.mdWelcomeRow}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              flexWrap: "wrap",
              gap: "1rem",
            }}
          >
            <div>
              <h2 className={styles.mdWelcomeTitle}>
                Welcome back, {currentUser?.firstName}
              </h2>
              <p className={styles.mdWelcomeSub}>
                Have a look at your balance and transactions
              </p>
            </div>
            <div className={styles.mdTccBox}>
              <span>{tcc}</span>
              <img
                className={styles.mdTccCopy}
                src={copyIcon}
                alt="Copy TCC"
                onClick={() => navigator.clipboard.writeText(tcc)}
              />
            </div>
          </div>
        </div>

        {/* ---- Balance Cards ---- */}
        <BalanceSummary
          availableBalance={availableBalance}
          refundableBalance={refundableBalance}
          pendingPayment={pendingPayment}
          status={status}
          loading={balanceLoading}
          onWithdrawalClick={openWithdrawalGate}
        />

        {/* ---- Quick Actions ---- */}
        <QuickActions
          onDepositClick={openDeposit}
          onWithdrawalClick={openWithdrawalGate}
        />

        {/* ---- Analytics ---- */}
        <TransactionMetrics transactions={transactions} loading={txLoading} />

        {/* ---- Recent Transactions ---- */}
        <RecentTransactions
          recentTransactions={recentTransactions}
          loading={txLoading}
        />
      </div>

      {/* ---- Modals (lazy loaded) ---- */}
      <Suspense fallback={null}>
        {showDeposit && (
          <DepositModal isOpen={showDeposit} onClose={closeDeposit} />
        )}
      </Suspense>

      <WithdrawalGate
        isOpen={showWithdrawalGate}
        onClose={closeWithdrawalGate}
        pendingPayment={pendingPayment}
        refundableBalance={refundableBalance}
        onPayNow={handlePayNow}
        onProceedToWithdraw={handleProceedToWithdraw}
      />

      {/* ---- Chat widget (lazy loaded) ---- */}
      <Suspense fallback={null}>
        <ChatWidget />
      </Suspense>
    </>
  );
}